import {
  BoundSelectField,
  Button,
  column,
  GridColumn,
  GridDataRow,
  GridTable,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  Tooltip,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { UppyFile } from "@uppy/core";
import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { UppyUploader } from "src/components/UppyUploader";
import { getStage } from "src/context";
import {
  LotPartitionDocuments_DocumentFragment,
  SaveDocumentInput,
  useLotPartitionDocumentsQuery,
  useSaveLotPartitionDocumentsMutation,
  DocumentType,
  LotPartitionDocuments_LotPartitionFragment,
  InputMaybe,
  Maybe,
  useSavePropertySaleSettlementStatementMutation,
  useProcessPropertySaleSettlementStatementOcrMutation,
} from "src/generated/graphql-types";
import { createPropertySaleSettlementStatementUrl } from "src/RouteUrls";
import { queryResult } from "src/utils/queryResult";
import { safeEntries } from "src/utils/utils";

export type LotPartitionDocumentsTableProps = {
  lotPartitionId: string;
  lotId: string;
};

export function LotPartitionDocumentsTable(props: LotPartitionDocumentsTableProps) {
  const { lotPartitionId } = props;
  const [allowDocRedirect, setAllowDocRedirect] = useState(true);

  const query = useLotPartitionDocumentsQuery({
    variables: { lotPartitionId },
    skip: !lotPartitionId,
  });

  const [saveDocuments] = useSaveLotPartitionDocumentsMutation();
  const [savePropertySaleSettlementStatement] = useSavePropertySaleSettlementStatementMutation();
  const [processPropertySaleSettlementStatementOcr] = useProcessPropertySaleSettlementStatementOcrMutation();

  // Once an asset has been uploaded, create a document with an asset
  async function handleOnFinish(file: UppyFile) {
    if (allowDocRedirect) {
      setAllowDocRedirect(false);
    }
    const saveDocumentsInput: [SaveDocumentInput] = [
      {
        parentId: lotPartitionId,
        asset: {
          contentType: file.type,
          fileName: file.name,
          s3Key: file.meta.s3Key as string,
          sizeInBytes: file.size,
        },
        documentType: DocumentType.General,
      },
    ];
    await saveDocuments({
      variables: { input: { documents: saveDocumentsInput } },
    });
    await query.refetch();
  }

  async function createPropertySaleSettlementStatement(document: LotPartitionDocuments_DocumentFragment) {
    const settlementStatement = await savePropertySaleSettlementStatement({
      variables: { input: { documentId: document.id, lotPartitionId } },
    });
    const { id } = settlementStatement.data?.savePropertySaleSettlementStatement.propertySaleSettlementStatement ?? {};
    if (id) {
      // Process the OCR for the new settlement statement
      await processPropertySaleSettlementStatementOcr({ variables: { input: { id } } });
      // Open it in a new tab
      window.open(createPropertySaleSettlementStatementUrl(document.id), "_blank", "noopener,noreferrer");
      // And refresh the page to show the new state
      await query.refetch();
    }
  }

  return queryResult(query, ({ documents, lotPartitions }) => {
    const lotPartition = lotPartitions[0];
    return (
      <LotPartitionDocumentsForm
        documents={documents}
        lotPartition={lotPartition}
        handleOnFinish={handleOnFinish}
        setAllowDocRedirect={setAllowDocRedirect}
        createPropertySaleSettlementStatement={createPropertySaleSettlementStatement}
      />
    );
  });
}

function LotPartitionDocumentsForm({
  documents,
  lotPartition,
  handleOnFinish,
  setAllowDocRedirect,
  createPropertySaleSettlementStatement,
}: {
  documents: LotPartitionDocuments_DocumentFragment[];
  lotPartition: LotPartitionDocuments_LotPartitionFragment;
  handleOnFinish: (file: UppyFile) => void;
  setAllowDocRedirect: (allow: boolean) => void;
  createPropertySaleSettlementStatement: (document: LotPartitionDocuments_DocumentFragment) => void;
}) {
  const [saveDocuments] = useSaveLotPartitionDocumentsMutation();

  async function saveLotPartitionDocuments(formState: ObjectState<LotPartitionDocumentsFormInput>) {
    const changedDocuments = formState.changedValue.documents?.filter((doc) => doc.documentType !== undefined) || [];
    const documentInput: SaveDocumentInput[] = changedDocuments.map((doc) => ({
      id: doc.id,
      documentType: doc.documentType,
    }));
    await saveDocuments({ variables: { input: { documents: documentInput } } });
  }

  const formState = useFormState({
    config: formConfig,
    init: {
      input: mapLotPartitionDocumentsToForm(lotPartition.id, documents),
    },
    autoSave: saveLotPartitionDocuments,
  });

  return (
    <>
      <UppyUploader onFinish={handleOnFinish} onComplete={() => setAllowDocRedirect(true)} />

      <GridTable
        id="lotPartitionDocumentsTable"
        rowStyles={rowStyles}
        columns={createColumns(formState, createPropertySaleSettlementStatement)}
        rows={createRows(documents)}
        fallbackMessage="Documents for this Lot Partition will show here."
      />
    </>
  );
}

export type LotPartitionDocumentsFormInput = {
  id: InputMaybe<string>;
  documents: {
    id: InputMaybe<string>;
    documentType: InputMaybe<DocumentType>;
  }[];
};

export const formConfig: ObjectConfig<LotPartitionDocumentsFormInput> = {
  id: { type: "value" },
  documents: {
    type: "list",
    config: {
      id: { type: "value" },
      documentType: { type: "value" },
    },
  },
};

function mapLotPartitionDocumentsToForm(
  lotPartitionId: string,
  documents: Maybe<LotPartitionDocuments_DocumentFragment[]>,
): LotPartitionDocumentsFormInput {
  return {
    id: lotPartitionId,
    documents: mapDocumentsToForm(documents),
  };
}

export function mapDocumentsToForm(documents: Maybe<LotPartitionDocuments_DocumentFragment[]>) {
  const docs = documents?.map((doc) => {
    return {
      id: doc.id,
      documentType: doc.documentType.code,
    };
  });
  return docs || [];
}

type Row = SimpleHeaderAndData<LotPartitionDocuments_DocumentFragment>;

const rowStyles: RowStyles<Row> = {
  header: {},
  data: {},
};

function createRows(documents: LotPartitionDocuments_DocumentFragment[]): GridDataRow<Row>[] {
  return simpleDataRows(documents);
}

function createColumns(
  formState: ObjectState<LotPartitionDocumentsFormInput>,
  createPropertySaleSettlementStatement: (document: LotPartitionDocuments_DocumentFragment) => void,
): GridColumn<Row>[] {
  const idColumn = column<Row>({
    header: () => "Document ID",
    data: ({ id }) => id,
    w: "100px",
  });

  const nameColumn = column<Row>({
    header: () => "Document Name",
    data: ({ asset }) => asset.fileName,
    w: "300px",
  });

  const downloadColumn = column<Row>({
    header: () => "",
    data: ({ asset }) => {
      return (
        <Button
          label="Download"
          variant="tertiary"
          onClick={() => {
            window.open(asset.downloadUrl, "_blank", "noopener,noreferrer");
          }}
        />
      );
    },
  });

  const typeColumn = column<Row>({
    header: () => "Type",
    data: (row) => {
      const formStateField = formState.documents.rows.find((doc) => doc.id.value === row.id);
      return {
        value: () => row.documentType,
        content: () => (
          <BoundSelectField
            field={formStateField!.documentType}
            options={safeEntries(DocumentType)}
            getOptionValue={([, code]) => code}
            getOptionLabel={([name]) => name}
          />
        ),
      };
    },
    w: "300px",
  });

  const actionButtonColumn = column<Row>({
    header: () => "",
    data: (document) => {
      if (document.canCreatePropertySaleSettlementStatement.allowed) {
        return (
          <Button
            label="Create Property Sale Settlement Statement"
            variant="secondary"
            onClick={async () => {
              await createPropertySaleSettlementStatement(document);
            }}
          />
        );
      } else if (document.canViewPropertySaleSettlementStatement.allowed) {
        return <Link to={createPropertySaleSettlementStatementUrl(document.id)}>View Settlement Statement</Link>;
      }
      const disabledReason =
        document.documentType.code === DocumentType.PropertySaleSettlementStatementFinal
          ? document.canCreatePropertySaleSettlementStatement.disabledReasons.map((reason) => reason.message).join("; ")
          : "";
      return <Tooltip title={disabledReason}>No actions available</Tooltip>;
    },
  });

  return [idColumn, nameColumn, downloadColumn, typeColumn, actionButtonColumn];
}
